import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useParams } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import LoadingScreen from '../common/LoadingScreen';

/**
 * OrganizationRemovalGuard - Detects when a user has been removed from an organization
 * and provides a clean sign-out option to prevent users from getting stuck
 */
const OrganizationRemovalGuard = ({ children }) => {
  const { getAccessTokenSilently, logout, user } = useAuth0();
  const navigate = useNavigate();
  const { orgUniqueId } = useParams();
  const [isChecking, setIsChecking] = useState(true);
  const [isRemoved, setIsRemoved] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkOrganizationAccess = async () => {
      try {
        setIsChecking(true);
        
        const token = await getAccessTokenSilently();
        
        // If no orgUniqueId in URL, let the component render normally
        // This handles routes that don't have organization context
        if (!orgUniqueId) {
          setIsRemoved(false);
          return;
        }
        
        // Check access to the specific organization in the URL
        // Use a more specific endpoint that doesn't rely on JWT claims
        const response = await fetch(`${API_BASE_URL}/api/organizations/check-access/${orgUniqueId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.status === 404) {
          // Organization doesn't exist or user has no access
          console.log('🚫 User has no access to organization:', orgUniqueId);
          setIsRemoved(true);
        } else if (response.status === 403) {
          // User has no access to this specific organization
          console.log('🚫 User forbidden from accessing organization:', orgUniqueId);
          setIsRemoved(true);
        } else if (response.ok) {
          // User has valid access to this organization
          console.log('✅ User has access to organization:', orgUniqueId);
          setIsRemoved(false);
        } else {
          // Other error - log it but don't block access (could be server issue)
          console.error('Organization access check failed:', response.status);
          setError(`Failed to verify organization access: ${response.status}`);
          // Don't set isRemoved=true for server errors, give user benefit of doubt
        }
      } catch (error) {
        console.error('Error checking organization access:', error);
        setError('Failed to verify organization access');
        // Don't block access on network errors - user might just have connectivity issues
      } finally {
        setIsChecking(false);
      }
    };

    checkOrganizationAccess();
  }, [getAccessTokenSilently, orgUniqueId]);

  const handleSignOut = () => {
    logout({
      logoutParams: {
        returnTo: window.location.origin
      }
    });
  };

  const handleRetry = () => {
    setError(null);
    setIsChecking(true);
    // Re-run the check
    window.location.reload();
  };

  // Show loading while checking
  if (isChecking) {
    return <LoadingScreen message="Verifying organization access..." />;
  }

  // Show error state with retry option
  if (error && !isRemoved) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.963-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Organization Access Check Failed
            </h3>
            <p className="text-sm text-gray-500 mb-6">
              {error}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleRetry}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                Retry
              </button>
              <button
                onClick={handleSignOut}
                className="flex-1 bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show removal notification with sign-out option
  if (isRemoved) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 mb-4">
              <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.963-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Organization Access Removed
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              You no longer have access to this organization. This may have happened because:
            </p>
            <ul className="text-sm text-gray-500 text-left mb-6 space-y-1">
              <li>• You were removed from the organization</li>
              <li>• Your account permissions were changed</li>
              <li>• The organization was deleted</li>
            </ul>
            <p className="text-sm text-gray-500 mb-6">
              Please contact your organization administrator if you believe this is an error.
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={handleSignOut}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                Sign Out & Return to Login
              </button>
              <button
                onClick={() => navigate('/')}
                className="w-full bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors"
              >
                Return to Home
              </button>
            </div>
            {user?.email && (
              <p className="mt-4 text-xs text-gray-400">
                Signed in as: {user.email}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // User has valid access - render children
  return children;
};

export default OrganizationRemovalGuard;
